import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient.js';

const UserContext = createContext({
  user: null,
  profile: null,
  organization: null,
  role: null,
  subscriptionInfo: null,
  loading: true,
  refreshUserData: async () => {}
});

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [role, setRole] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = async (sessionUser) => {
    setLoading(true);
    if (!sessionUser) {
      setUser(null);
      setProfile(null);
      setOrganizationId(null);
      setRole(null);
      setSubscriptionInfo(null);
      setLoading(false);
      return;
    }

    setUser(sessionUser);

    try {
      // Fetch profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profile')
        .select('first_name, last_name, is_email_confirmed')
        .eq('id', sessionUser.id)
        .single();
      if (profileError) throw profileError;
      setProfile(profileData);

      // --- THE FIX ---
      // We now use .limit(1).maybeSingle() to gracefully handle both ZERO and MANY results.
      // This prevents the app from crashing if a user is in more than one organization.
      const { data: orgMemberData, error: orgMemberError } = await supabase
        .from('organization_members')
        .select('organization_id, role')
        .eq('user_id', sessionUser.id)
        .limit(1) // Return at most one record
        .maybeSingle(); // Return the record or null, but don't error

      // Throw any real database errors
      if (orgMemberError) throw orgMemberError;
      
      const currentOrgId = orgMemberData?.organization_id || null;
      setOrganizationId(currentOrgId);
      setRole(orgMemberData?.role || null);

      if (currentOrgId) {
        const { data: subData, error: subError } = await supabase
          .from('organization_subscriptions')
          .select('*')
          .eq('id', currentOrgId)
          .single();
        if (subError) throw subError;
        setSubscriptionInfo({
          status: subData.subscription_status,
          tier: subData.subscription_tier,
          trialEndDate: subData.trial_end_date,
          companyName: subData.name,
          currentPeriodEnd: subData.current_period_end,
          currentMembers: subData.current_member_count,
          maxMembers: subData.max_member_count,
        });
      } else {
        setSubscriptionInfo(null);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setProfile(null);
      setOrganizationId(null);
      setRole(null);
      setSubscriptionInfo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
        fetchUserData(session?.user);
      } else if (event === 'SIGNED_OUT') {
        fetchUserData(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <UserContext.Provider value={{ user, profile, organization: organizationId, role, subscriptionInfo, loading, refreshUserData: fetchUserData }}>
      {children}
    </UserContext.Provider>
  );
};